// src/features/clinical/components/prescription/PrescriptionCreation.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaTrash, FaFlask, FaNotesMedical, FaCalendarPlus, FaSearch } from 'react-icons/fa';
import toast from 'react-hot-toast';
import clinicalApi from '../../services/clinicalApi';
import MedicineSelector from './MedicineSelector';
import TestSelector from './TestSelector';
import DoctorNotes from '../common/DoctorNotes';
import debounce from 'lodash/debounce';

const PrescriptionCreation = ({ visitId, patientId, patientName, doctorId, onComplete }) => {
  const [medicines, setMedicines] = useState([]);
  const [tests, setTests] = useState([]);
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // For search functionality
  const [medicineSearchTerms, setMedicineSearchTerms] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [searching, setSearching] = useState({});
  const [availableMedicines, setAvailableMedicines] = useState([]);
  const [availableTests, setAvailableTests] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [medicinesRes, testsRes] = await Promise.all([
        clinicalApi.getMedicines(),
        clinicalApi.getTests()
      ]);
      setAvailableMedicines(medicinesRes.data);
      setAvailableTests(testsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  // Debounced search function
  const searchMedicines = useCallback(
    debounce(async (index, searchTerm) => {
      if (searchTerm.length < 2) {
        setSearchResults(prev => ({ ...prev, [index]: [] }));
        setSearching(prev => ({ ...prev, [index]: false }));
        return;
      }

      setSearching(prev => ({ ...prev, [index]: true }));
      
      try {
        // If you have a search API endpoint
        const response = await clinicalApi.getMedicines(searchTerm);
        setSearchResults(prev => ({ ...prev, [index]: response.data }));
        console.log(response.data);
      
        
        // Client-side filtering as fallback
        // const filtered = availableMedicines.filter(med => 
        //   med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        //   med.strength?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        //   med.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase())
        // );
        // setSearchResults(prev => ({ ...prev, [index]: filtered }));
      } catch (error) {
        console.error('Search failed:', error);
        toast.error('Failed to search medicines');
      } finally {
        setSearching(prev => ({ ...prev, [index]: false }));
      }
    }, 300),
    [availableMedicines]
  );

  const handleMedicineSearchChange = (index, value) => {
    setMedicineSearchTerms(prev => ({ ...prev, [index]: value }));
    searchMedicines(index, value);
  };

  const selectMedicine = (index, medicine) => {
    // Update medicine with selected values
    const updated = [...medicines];
    updated[index] = {
      ...updated[index],
      medicineId: medicine.id,
      medicineName: medicine.name,
      dosage: medicine.strength || '',
      // Store additional medicine info if needed
      medicineDetails: medicine
    };
    setMedicines(updated);

    // Clear search
    setMedicineSearchTerms(prev => ({ ...prev, [index]: '' }));
    setSearchResults(prev => ({ ...prev, [index]: [] }));
  };

  const addMedicine = () => {
    const newIndex = medicines.length;
    setMedicines([
      ...medicines,
      {
        medicineId: '',
        medicineName: '',
        dosage: '',
        frequency: '',
        duration: '',
        quantity: 1,
        route: 'Oral',
        instructions: '',
        substitutionAllowed: true,
        temporaryId: Date.now() + Math.random()
      }
    ]);
    // Initialize search state for new medicine
    setMedicineSearchTerms(prev => ({ ...prev, [newIndex]: '' }));
    setSearchResults(prev => ({ ...prev, [newIndex]: [] }));
    setSearching(prev => ({ ...prev, [newIndex]: false }));
  };

  const removeMedicine = (index) => {
    setMedicines(medicines.filter((_, i) => i !== index));
    
    // Clean up search state
    const newSearchTerms = { ...medicineSearchTerms };
    const newSearchResults = { ...searchResults };
    const newSearching = { ...searching };
    
    delete newSearchTerms[index];
    delete newSearchResults[index];
    delete newSearching[index];
    
    // Re-index remaining items
    const reindexed = {};
    medicines.filter((_, i) => i !== index).forEach((_, newIndex) => {
      if (newSearchTerms[newIndex + 1]) {
        reindexed[newIndex] = newSearchTerms[newIndex + 1];
      }
      if (newSearchResults[newIndex + 1]) {
        setSearchResults(prev => ({ ...prev, [newIndex]: newSearchResults[newIndex + 1] }));
      }
      if (newSearching[newIndex + 1]) {
        setSearching(prev => ({ ...prev, [newIndex]: newSearching[newIndex + 1] }));
      }
    });
    
    setMedicineSearchTerms(reindexed);
  };

  const updateMedicine = (index, field, value) => {
    const updated = [...medicines];
    updated[index][field] = value;
    
    // Auto-calculate quantity if dosage, frequency, and duration are set
    if (field === 'dosage' || field === 'frequency' || field === 'duration') {
      const medicine = updated[index];
      if (medicine.frequency && medicine.duration) {
        const frequencyMap = {
          'Once daily': 1,
          'Twice daily': 2,
          'Three times daily': 3,
          'Four times daily': 4,
          'Every 4 hours': 6,
          'Every 6 hours': 4,
          'Every 8 hours': 3,
          'At bedtime': 1,
          'Once daily before meals': 1,
          'As needed': 2
        };
        const timesPerDay = frequencyMap[medicine.frequency] || 1;
        const days = parseInt(medicine.duration) || 1;
        updated[index].quantity = timesPerDay * days;
      }
    }
    
    setMedicines(updated);
  };

  const addTest = () => {
    setTests([
      ...tests,
      {
        testId: '',
        instructions: '',
        priority: 'ROUTINE',
        temporaryId: Date.now() + Math.random()
      }
    ]);
  };

  const removeTest = (index) => {
    setTests(tests.filter((_, i) => i !== index));
  };

  const updateTest = (index, field, value) => {
    const updated = [...tests];
    updated[index][field] = value;
    setTests(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (medicines.length === 0 && tests.length === 0) {
      toast.error('Please add at least one medicine or test');
      return;
    }

    // Validate medicines
    for (const med of medicines) {
      if (!med.medicineId || !med.dosage || !med.frequency || !med.duration) {
        toast.error('Please complete all medicine details');
        return;
      }
    }

    setSubmitting(true);
    
    try {
      const prescriptionData = {
        visitId,
        patientId,
        doctorId,
        medicines: medicines.map(({ temporaryId, medicineDetails, ...med }) => ({
          ...med,
          medicineId: med.medicineId
        })),
        tests: tests.map(({ temporaryId, ...test }) => test),
        notes,
        followUpDate: followUpDate || null,
        status: 'ACTIVE'
      };
      
      await clinicalApi.createPrescription(prescriptionData);
      toast.success('Prescription created successfully');
      
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      toast.error('Failed to create prescription');
    } finally {
      setSubmitting(false);
    }
  };

  const frequencyOptions = [
    'Once daily',
    'Twice daily',
    'Three times daily',
    'Four times daily',
    'Every 4 hours',
    'Every 6 hours',
    'Every 8 hours',
    'At bedtime',
    'Once daily before meals',
    'As needed'
  ];

  const routeOptions = ['Oral', 'Intravenous', 'Intramuscular', 'Topical', 'Inhalation', 'Sublingual', 'As prescribed'];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Create Prescription</h2>
      <p className="text-gray-600 mb-4">Patient: {patientName}</p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Medicines Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Medicines</h3>
            <button
              type="button"
              onClick={addMedicine}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
            >
              <FaPlus className="mr-2" /> Add Medicine
            </button>
          </div>

          {medicines.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No medicines added</p>
          ) : (
            <div className="space-y-4">
              {medicines.map((medicine, index) => (
                <div key={medicine.temporaryId} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-medium text-gray-900">Medicine #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeMedicine(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    {/* Medicine Search Field */}
                    <div className="col-span-3 relative">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Medicine *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          required
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                          placeholder="Type at least 2 letters to search..."
                          value={medicineSearchTerms[index] || medicine.medicineName || ''}
                          onChange={(e) => handleMedicineSearchChange(index, e.target.value)}
                        />
                        <FaSearch className="absolute right-2 top-2 text-gray-400" />
                      </div>
                      
                      {/* Search Results Dropdown */}
                      {medicineSearchTerms[index]?.length >= 3 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {searching[index] ? (
                            <div className="p-2 text-sm text-gray-500">Searching...</div>
                          ) : searchResults[index]?.length > 0 ? (
                            searchResults[index].map((med) => (
                              <div
                                key={med.id}
                                className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                                onClick={() => selectMedicine(index, med)}
                              >
                                <div className="font-medium text-sm">{med.name}</div>
                                <div className="text-xs text-gray-600">
                                  {med.strength} | {med.manufacturer} | Stock: {med.total_available} {med.unit}s
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="p-2 text-sm text-gray-500">No medicines found</div>
                          )}
                        </div>
                      )}
                      
                      {/* Hidden select to maintain form compatibility */}
                      <select
                        required
                        className="hidden"
                        value={medicine.medicineId}
                        onChange={() => {}}
                      >
                        <option value="">Select Medicine</option>
                        {availableMedicines.map(med => (
                          <option key={med.id} value={med.id}>
                            {med.name} - {med.strength}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Dosage *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={medicine.dosage}
                        onChange={(e) => updateMedicine(index, 'dosage', e.target.value)}
                        placeholder="e.g., 500mg"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Frequency *
                      </label>
                      <select
                        required
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={medicine.frequency}
                        onChange={(e) => updateMedicine(index, 'frequency', e.target.value)}
                      >
                        <option value="">Select Frequency</option>
                        {frequencyOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Duration (days) *
                      </label>
                      <input
                        type="number"
                        required
                        min="1"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={medicine.duration}
                        onChange={(e) => updateMedicine(index, 'duration', e.target.value)}
                        placeholder="7"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        readOnly
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-100"
                        value={medicine.quantity || ''}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Route
                      </label>
                      <select
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={medicine.route}
                        onChange={(e) => updateMedicine(index, 'route', e.target.value)}
                      >
                        {routeOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Instructions
                      </label>
                      <input
                        type="text"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={medicine.instructions}
                        onChange={(e) => updateMedicine(index, 'instructions', e.target.value)}
                        placeholder="e.g., Take after meals"
                      />
                    </div>

                    <div className="col-span-3">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={medicine.substitutionAllowed}
                          onChange={(e) => updateMedicine(index, 'substitutionAllowed', e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">Generic substitution allowed</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tests Section */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FaFlask className="mr-2 text-purple-600" />
              Recommended Tests
            </h3>
            <button
              type="button"
              onClick={addTest}
              className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
            >
              <FaPlus className="mr-2" /> Add Test
            </button>
          </div>

          {tests.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No tests added</p>
          ) : (
            <div className="space-y-3">
              {tests.map((test, index) => (
                <div key={test.temporaryId} className="flex items-center space-x-3 bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <select
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={test.testId}
                      onChange={(e) => updateTest(index, 'testId', e.target.value)}
                    >
                      <option value="">Select Test</option>
                      {availableTests.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>

                    <select
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={test.priority}
                      onChange={(e) => updateTest(index, 'priority', e.target.value)}
                    >
                      <option value="ROUTINE">Routine</option>
                      <option value="URGENT">Urgent</option>
                      <option value="STAT">STAT</option>
                    </select>

                    <input
                      type="text"
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={test.instructions}
                      onChange={(e) => updateTest(index, 'instructions', e.target.value)}
                      placeholder="Special instructions"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTest(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Doctor Notes */}
        <DoctorNotes
          notes={notes}
          onNotesChange={setNotes}
        />

        {/* Follow-up */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <label className="flex items-center space-x-3">
            <FaCalendarPlus className="text-blue-600" />
            <span className="font-medium text-gray-700">Follow-up Date</span>
            <input
              type="date"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onComplete}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Complete Prescription'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PrescriptionCreation;